import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import SubmitIcon from "@material-ui/icons/Save";
import { httpClient } from "api/httpClient";
import { DateFormatField, TimeFormatField } from "components/fields/DateFormatField";
import { EditableGridCellField } from "components/fields/EditableGridCellField";
import { PhoneField } from "components/fields/PhoneField";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { OrdersDetailsType } from "entities/OrdersDetailsType";
import { keyBy } from "lodash";
import React, { useEffect, useState } from "react";
import {
	Button,
	Confirm,
	NumberField,
	NumberInput,
	ReferenceField,
	ReferenceInput,
	SelectInput,
	TextField,
	TopToolbar,
	sanitizeListRestProps,
	useRefresh,
	useTranslate,
} from "react-admin";
import { isNullUndEmpty } from "utils/general";
import { isEmptyObject } from "utils/validation/required";

import { DateInput, PhoneInput, TimeInput, YesNoInput } from "../../../components/inputs";
import { CommodityChoices, LabelSource, OrderServicesActiveNames } from "../../../entities";
import { useNotifyError } from "../../../utils/notifiers/useNotifyError";
import { useNotifySuccess } from "../../../utils/notifiers/useNotifySuccess";
import { FakeReferenceTextField } from "../../fields/FakeReferenceTextField";
import { SelectEnumField } from "../../fields/SelectEnumField";
import { YesNoField } from "../../fields/YesNoField";
import { WarningField, WarningList } from "../../HighlightList";
import { ToolbarButton } from "../OrdersExportDialog/ToolbarButton";

const isDetailsContainError = (details) => {
	const detailsValues = Object.values(details);
	return detailsValues.some((item) => item.type === OrdersDetailsType.error);
};

const allowReplaceSource = (record, source, replaceSource) =>
	!isNullUndEmpty(record[replaceSource]);

const PreviewDialog = ({ onDialogClose, sharedData, setSharedData }) => {
	const t = useTranslate();
	const refresh = useRefresh();
	const notifyError = useNotifyError();
	const notifySuccess = useNotifySuccess();

	const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
	const [createLoading, setCreateLoading] = useState(false);
	const [submitDisabled, setSubmitDisabled] = useState(false);

	const ordersWithWarningsData = sharedData?.data || [];
	const setSharedDataPreviewData = (data) => setSharedData({ ...sharedData, data: data });
	const onlyOrdersWithDetails = ordersWithWarningsData.filter((item) =>
		isDetailsContainError(item.details_info)
	);
	const allowSubmit = onlyOrdersWithDetails.length < ordersWithWarningsData.length;
	const allowConfirmation = allowSubmit && onlyOrdersWithDetails.length > 0;
	const extraProps = {
		mapper_type: sharedData?.mapper_type || null,
		order_import_id: sharedData?.order_import_id || null,
		company_id: sharedData?.company_id || null,
		split_orders_type: sharedData?.split_orders_type || false,
		add_loading_order_to_ref: sharedData?.add_loading_order_to_ref || false,
		attach_source_doc: sharedData?.attach_source_doc || false,
		split_document_content: sharedData?.split_document_content || false,
		setSubmitDisabled,
		setSharedDataPreviewData,
		isExistWarnings: onlyOrdersWithDetails.length > 0,
	};

	useEffect(() => {
		setSubmitDisabled(!allowSubmit);
	}, [allowSubmit]);

	const orderCreate = () => {
		const _data = {
			order_import_id: extraProps.order_import_id,
			attach_source_doc: extraProps.attach_source_doc,
			split_document_content: extraProps.split_document_content,
			split_orders_type: extraProps.split_orders_type,
			order_items: ordersWithWarningsData.filter(
				(item) => !isDetailsContainError(item.details_info)
			),
		};
		setCreateLoading(true);
		httpClient("orders-upload-save", { method: "POST", body: JSON.stringify(_data) })
			.then(() => {
				notifySuccess(t("osfc.dialogs.orders-import-preview.msg.success"));
				setSharedData({});
				onDialogClose();
				refresh();
			})
			.catch((error) => {
				notifyError(error);
			})
			.finally(() => {
				setCreateLoading(false);
			});
	};

	const handleConfirmOnClose = () => setSubmitConfirmOpen(false);
	const handleConfirmOnOpen = () => setSubmitConfirmOpen(true);

	const handleOnSubmitConfirm = () => {
		handleConfirmOnClose();
		orderCreate();
	};

	const handleOnSubmit = () => {
		if (!allowSubmit) return;
		if (allowConfirmation) {
			handleConfirmOnOpen();
			return;
		}
		orderCreate();
	};

	return (
		<Dialog open maxWidth="xl" onClose={onDialogClose} fullWidth scroll="body">
			<DialogTitle>{t("osfc.dialogs.orders-import-preview.title")}</DialogTitle>
			<DialogContent>
				<PreviewList data={ordersWithWarningsData} extraProps={extraProps} />
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={createLoading}
					label={t("ra.action.cancel")}
					onClick={onDialogClose}
					color="secondary"
				/>
				<Button
					variant="contained"
					label={t("osfc.buttons.submit")}
					onClick={handleOnSubmit}
					color="primary"
					disabled={submitDisabled}
					startIcon={
						createLoading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : (
							<SubmitIcon />
						)
					}
				/>
				{allowConfirmation && (
					<Confirm
						isOpen={submitConfirmOpen}
						content="osfc.dialogs.orders-import-preview.dialogs.confirm.content"
						title="osfc.dialogs.orders-import-preview.dialogs.confirm.title"
						onConfirm={handleOnSubmitConfirm}
						onClose={handleConfirmOnClose}
					/>
				)}
			</DialogActions>
		</Dialog>
	);
};

const margeData = (left, right) => {
	const mainData = { ...left };
	const mergeData = { ...right };
	Object.keys(mergeData).forEach((key) => {
		if (mainData.hasOwnProperty(key)) {
			mainData[key] = { ...mainData[key], ...mergeData[key] };
		}
	});
	return mainData;
};

export const PreviewListActions = (props) => {
	const { onEdit, isEdit, extraProps = {}, ...rest } = props;

	const translate = useTranslate();
	const notifySuccess = useNotifySuccess();
	const notifyError = useNotifyError();
	const [validateLoading, setValidateLoading] = useState(false);

	if (!props.defaultData && !isEmptyObject(props.data)) {
		props.setDefaultData(structuredClone(props.data));
	}

	const orderValidate = () => {
		const _data = {
			order_import_id: extraProps.order_import_id,
			mapper_type: extraProps.mapper_type,
			order_items: Object.values(margeData(props.data, props.selectedFields)),
			company_id: extraProps.company_id,
			split_orders_type: extraProps.split_orders_type,
			add_loading_order_to_ref: extraProps.add_loading_order_to_ref,
			attach_source_doc: extraProps.attach_source_doc,
			split_document_content: extraProps.split_document_content,
		};
		setValidateLoading(true);
		httpClient("orders-upload-validate", { method: "POST", body: JSON.stringify(_data) })
			.then(({ json }) => {
				notifySuccess(translate("ra.notification.updated", { smart_count: 1 }), {
					type: "info",
					messageArgs: { smart_count: 1 },
				});
				props.cancelEdit();
				extraProps?.setSharedDataPreviewData?.(json.order_items);
				props.setDefaultData(null);
			})
			.catch((error) => {
				notifyError(error);
			})
			.finally(() => {
				setValidateLoading(false);
			});
	};

	const onCancel = () => {
		props.cancelEdit();
		Object.assign(props.data, props.defaultData);
		props.setDefaultData(null);
	};

	const applyDisabled = validateLoading || isEmptyObject(props.selectedFields);

	return (
		<TopToolbar {...sanitizeListRestProps(rest)}>
			{!isEdit ? (
				<ToolbarButton onClick={onEdit} disabled={extraProps?.isExistWarnings === false}>
					{translate("osfc.buttons.edit")}
				</ToolbarButton>
			) : null}
			{isEdit ? (
				<ToolbarButton
					onClick={orderValidate}
					disabled={applyDisabled}
					icon={
						validateLoading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : (
							<SubmitIcon />
						)
					}
				>
					{translate("osfc.buttons.apply_changes")}
				</ToolbarButton>
			) : null}
			{isEdit ? (
				<ToolbarButton onClick={onCancel} disabled={validateLoading}>
					{translate("osfc.buttons.cancel")}
				</ToolbarButton>
			) : null}
		</TopToolbar>
	);
};

export const PreviewList = ({ data, extraProps = {} }) => {
	const [ids, setIds] = React.useState();
	const [dicData, setDicData] = React.useState();
	const [selectedIds, setSelectedIds] = React.useState([]);
	const [selectedFields, setSelectedFields] = React.useState({});
	const [defaultData, setDefaultData] = React.useState(null);
	const [isEdit, setIsEdit] = React.useState(false);

	const visible = dicData && ids;

	useEffect(() => {
		if (data) {
			const _data = data.map((d, index) => ({ ...d, id: index + 1 }));
			const _ids = _data.map((x) => x["id"]);
			setIds(_ids);
			const _dicData = keyBy(_data, "id");
			setDicData(_dicData);
		} else {
			setIds(undefined);
			setDicData(undefined);
		}
	}, [data]);

	const listValue = { ids: ids, data: dicData, currentSort: { field: "id", order: "ASC" } };

	const onEdit = () => {
		setIsEdit(true);
		extraProps?.setSubmitDisabled?.(true);
	};

	const updateSelectedIds = (newId) => {
		setSelectedIds((prevSelectedIds) => {
			if (prevSelectedIds.includes(newId)) {
				return prevSelectedIds;
			}
			return [...prevSelectedIds, newId];
		});
	};

	const updateSelectedFields = (newField, source) => {
		setSelectedFields((prevFields) => {
			prevFields[newField.id] = { ...prevFields[newField.id], [source]: newField[source] };
			return prevFields;
		});
	};

	const cancelEdit = () => {
		setSelectedIds([]);
		setSelectedFields({});
		setIsEdit(false);
		extraProps?.setSubmitDisabled?.(false);
	};

	const sharedFieldsProps = {
		editableFieldProps: {
			editable: isEdit,
			selectedIds,
			updateSelectedIds,
			selectedFields,
			updateSelectedFields,
		},
	};

	return visible ? (
		<PreviewListDataGrid
			value={listValue}
			isEdit={isEdit}
			sharedFieldsProps={sharedFieldsProps}
			Actions={
				<PreviewListActions
					data={dicData}
					selectedIds={selectedIds}
					cancelEdit={cancelEdit}
					selectedFields={selectedFields}
					onEdit={onEdit}
					isEdit={isEdit}
					defaultData={defaultData}
					setDefaultData={setDefaultData}
					extraProps={extraProps}
				/>
			}
		/>
	) : (
		<></>
	);
};

const editableStyles = { border: "3px dotted", minHeight: "20px" };
const editableFieldColor = "gray";

const TextEditableField = (props) => (
	<EditableGridCellField {...props} style={editableStyles} changesFieldColor={editableFieldColor} />
);

const ReferenceEditableField = (props) => {
	const { inputFieldProps = {}, fieldFieldProps = {}, ...rest } = props;

	const EditInputComponent = (inputProps) => {
		const { renderFieldProps, ...restInputFieldProps } = inputFieldProps;

		return (
			<ReferenceInput {...inputProps} {...restInputFieldProps}>
				<SelectInput {...renderFieldProps} />
			</ReferenceInput>
		);
	};

	const EditFieldComponent = (fieldProps) => {
		const { renderFieldProps, optimized = false, ...restFieldFieldProps } = fieldFieldProps;

		if (optimized) {
			return (
				<div {...fieldProps} {...restFieldFieldProps}>
					<TextField {...renderFieldProps} />
				</div>
			);
		}

		return (
			<ReferenceField {...fieldProps} {...restFieldFieldProps}>
				<TextField {...renderFieldProps} />
			</ReferenceField>
		);
	};

	return (
		<TextEditableField
			{...rest}
			EditInputComponent={EditInputComponent}
			EditFieldComponent={EditFieldComponent}
		/>
	);
};

const ReferenceTextEditableField = (props) => {
	const { commonFieldProps = {}, ...rest } = props;
	const { mainSource, referenceSource } = commonFieldProps;

	if (rest.source === mainSource) {
		return <TextEditableField {...rest} />;
	} else if (rest.source === referenceSource) {
		return <ReferenceEditableField {...rest} />;
	}

	return null;
};

const DateEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={DateInput}
		EditFieldComponent={DateFormatField}
	/>
);

const TimeEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={TimeInput}
		EditFieldComponent={TimeFormatField}
	/>
);

const YesNoEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={YesNoInput} EditFieldComponent={YesNoField} />
);

const NumberEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={NumberInput} EditFieldComponent={NumberField} />
);

const SelectEnumEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={SelectEnumInput}
		EditFieldComponent={SelectEnumField}
	/>
);

const PhoneEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={PhoneInput} EditFieldComponent={PhoneField} />
);

const PreviewListDataGrid = (props) => (
	<WarningList {...props}>
		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "customer-companies",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "full_name" },
					filter: { no_range: true, is_blocked: false, is_deleted: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "full_name" },
				},
			}}
			source="company_id"
			label="resources.orders.fields.company_id"
			replaceSource="customer_name"
		/>

		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="priority"
			label="resources.orders.fields.priority"
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="reference"
			label="resources.orders.fields.reference"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={SelectEnumEditableField}
			editableComponentsProps={{
				translate: false,
				allowEmpty: true,
				reverse: true,
				enumObj: OrderServicesActiveNames,
			}}
			replaceSource="service_name"
			source="service"
			label="resources.orders.fields.service"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "terminal",
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="terminal_id"
			label="resources.orders.fields.terminal"
			replaceSource="terminal_name"
		/>

		<WarningField
			component={TextField}
			editableComponent={DateEditableField}
			source="eta_date"
			label="resources.orders.fields.eta_date"
		/>
		<WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="eta_time"
			label="resources.orders.fields.eta_time"
		/>
		<WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="eta_slot_time"
			label="resources.orders.fields.eta_slot_time"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "ports",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					disableValue: "disabled_by_any",
					filter: (rec) => ({ terminal_id: [rec.terminal_id] }),
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="port_in"
			label="resources.orders.fields.port_in"
			replaceSource="port_in_name"
		/>
		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "transporters",
				inputFieldProps: {
					renderFieldProps: { optionText: "full_name" },
					filter: { no_range: true, is_blocked: false, is_deleted: false, all_transporters: true },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "full_name" },
				},
			}}
			source="eta_3rd_party"
			label="resources.orders.fields.eta_3rd_party"
			replaceSource="eta_3rd_party_name"
		/>

		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="eta_show_doc_for_trans"
			label="resources.orders.fields.eta_show_doc"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "drivers",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "first_name" },
				},
				commonFieldProps: {
					mainSource: "eta_driver",
					referenceSource: "eta_driver_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="eta_driver|eta_driver_id"
			label="resources.orders.fields.eta_driver"
			replaceSource="eta_driver_name"
		/>

		<WarningField
			component={PhoneField}
			editableComponent={PhoneEditableField}
			source="eta_driver_phone"
			label="resources.orders.fields.eta_driver_phone"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "trucks",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
				commonFieldProps: {
					mainSource: "eta_truck",
					referenceSource: "eta_truck_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="eta_truck|eta_truck_id"
			label="resources.orders.fields.eta_truck"
			replaceSource="eta_truck_name"
		/>
		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "trailers",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
				commonFieldProps: {
					mainSource: "eta_trailer",
					referenceSource: "eta_trailer_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="eta_trailer|eta_trailer_id"
			label="resources.orders.fields.eta_trailer"
			replaceSource="eta_trailer_name"
		/>

		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="place_from"
			label="resources.orders.fields.place_from"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_eta_3rd_party_reload"
			label="resources.orders.fields.is_eta_3rd_party_reload"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "company-users",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="assignee_1"
			label="resources.orders.fields.assignee_1"
			replaceSource="assignee_1_name"
		/>

		<WarningField
			component={TextField}
			editableComponent={DateEditableField}
			source="etd_date"
			label="resources.orders.fields.etd_date"
		/>
		<WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="etd_time"
			label="resources.orders.fields.etd_time"
		/>
		<WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="etd_slot_time"
			label="resources.orders.fields.etd_slot_time"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "ports",
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					disableValue: "disabled_by_any",
					filter: (rec) => ({ terminal_id: [rec.terminal_id] }),
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="port_out"
			label="resources.orders.fields.port_out"
			replaceSource="port_out_name"
		/>
		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "transporters",
				inputFieldProps: {
					renderFieldProps: { optionText: "full_name" },
					filter: { no_range: true, is_blocked: false, is_deleted: false, all_transporters: true },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "full_name" },
				},
			}}
			source="etd_3rd_party"
			label="resources.orders.fields.etd_3rd_party"
			replaceSource="etd_3rd_party_name"
		/>

		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="etd_show_doc_for_trans"
			label="resources.orders.fields.etd_show_doc"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "drivers",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "first_name" },
				},
				commonFieldProps: {
					mainSource: "etd_driver",
					referenceSource: "etd_driver_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="etd_driver|etd_driver_id"
			label="resources.orders.fields.etd_driver"
			replaceSource="etd_driver_name"
		/>

		<WarningField
			component={PhoneField}
			editableComponent={PhoneEditableField}
			source="etd_driver_phone"
			label="resources.orders.fields.etd_driver_phone"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "trucks",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
				commonFieldProps: {
					mainSource: "etd_truck",
					referenceSource: "etd_truck_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="etd_truck|etd_truck_id"
			label="resources.orders.fields.etd_truck"
			replaceSource="etd_truck_name"
		/>
		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceTextEditableField}
			editableComponentsProps={{
				reference: "trailers",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
				commonFieldProps: {
					mainSource: "etd_trailer",
					referenceSource: "etd_trailer_id",
				},
			}}
			allowReplaceSource={allowReplaceSource}
			source="etd_trailer|etd_trailer_id"
			label="resources.orders.fields.etd_trailer"
			replaceSource="etd_trailer_name"
		/>

		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="place_to"
			label="resources.orders.fields.place_to"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_etd_3rd_party_reload"
			label="resources.orders.fields.is_etd_3rd_party_reload"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "company-users",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="assignee_2"
			label="resources.orders.fields.assignee_2"
			replaceSource="assignee_2_name"
		/>

		<WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="boxes"
			label="resources.orders.fields.boxes"
		/>
		<WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="label"
			label="resources.orders.fields.label"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={SelectEnumEditableField}
			editableComponentsProps={{
				enumName: "label_source",
				enumObj: LabelSource,
			}}
			replaceSource="label_source_name"
			source="label_source"
			label="resources.orders.fields.label_source"
		/>

		<WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="pallets"
			label="resources.orders.fields.pallets"
		/>
		<WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="kilos"
			label="resources.orders.fields.kilos"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_doc_for_nfsa"
			label="resources.orders.fields.is_doc_for_nfsa"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={SelectEnumEditableField}
			editableComponentsProps={{
				enumObj: CommodityChoices,
				enumName: "commodity",
			}}
			source="commodity"
			label="resources.orders.fields.commodity"
			replaceSource="commodity_name"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "cooler-types",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="cooler_type_id"
			label="resources.orders.fields.cooler_type_id"
			replaceSource="cooler_type_name"
		/>

		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="split_load"
			label="resources.orders.fields.split_load"
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="trip_number"
			label="resources.orders.fields.trip_number"
		/>
		<WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="loading_order"
			label="resources.orders.fields.loading_order"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="plumb_seal"
			label="resources.orders.fields.plumb_seal"
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="printed_doc"
			label="resources.orders.fields.printed_doc"
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="extra_loading_time"
			label="resources.orders.fields.extra_loading_time"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_extra_loading_time_billable"
			label="resources.orders.fields.is_extra_loading_time_billable"
		/>
		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_area_blocked"
			label="resources.orders.fields.is_area_blocked"
		/>

		<WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "areas",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: "name" },
					filter: { disabled: false },
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="area_id"
			label="resources.orders.fields.area_id"
			replaceSource="area_name"
		/>

		<WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="set_order_completed"
			label="resources.orders.fields.set_order_completed"
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="notes"
			label="resources.orders.fields.notes"
			maxWidth={100}
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="internal_notes"
			label="resources.orders.fields.internal_notes"
			maxWidth={100}
		/>
	</WarningList>
);

export { PreviewDialog as default };
